/**
 * Coach-mark geometry (#776), pure so it is unit-tested. Rects are viewport coordinates (getBoundingClientRect).
 */
export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  w: number;
  h: number;
}

/** Spotlight padding around the target, gap between spotlight and card, minimum distance to the viewport edge. */
export const SPOT_PAD = 6;
export const GAP = 10;
export const EDGE = 12;
/** Below this viewport width the card is a bottom sheet instead of a floating card. */
export const PHONE_MAX_WIDTH = 640;

export type Placement =
  | { kind: 'floating'; side: 'below' | 'above' | 'right' | 'left'; top: number; left: number }
  | { kind: 'corner' };

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

/** Where a floating card of `card` size goes next to `target` in a `vw`×`vh` viewport: below, above, right, left, or the corner. */
export function placeCard(target: Rect, card: Size, vw: number, vh: number): Placement {
  const top = target.top - SPOT_PAD;
  const bottom = target.top + target.height + SPOT_PAD;
  const left = target.left - SPOT_PAD;
  const right = target.left + target.width + SPOT_PAD;
  const alignX = clamp(target.left, EDGE, vw - card.w - EDGE);
  const alignY = clamp(target.top, EDGE, vh - card.h - EDGE);

  if (bottom + GAP + card.h <= vh - EDGE) return { kind: 'floating', side: 'below', top: bottom + GAP, left: alignX };
  if (top - GAP - card.h >= EDGE) return { kind: 'floating', side: 'above', top: top - GAP - card.h, left: alignX };
  if (right + GAP + card.w <= vw - EDGE) return { kind: 'floating', side: 'right', top: alignY, left: right + GAP };
  if (left - GAP - card.w >= EDGE) return { kind: 'floating', side: 'left', top: alignY, left: left - GAP - card.w };
  return { kind: 'corner' };
}

/**
 * How far to scroll (window.scrollBy top) so the target is visible between `topInset` (sticky header) and
 * `vh - bottomInset` (phone bottom sheet). 0 when it already is. Targets taller than the free area are top-aligned,
 * shorter ones centred (desktop) or top-aligned (phone, where the sheet covers the bottom).
 */
export function scrollDelta(target: Rect, vh: number, topInset: number, bottomInset: number, centre: boolean): number {
  const freeTop = topInset + EDGE;
  const freeBottom = vh - bottomInset - EDGE;
  const free = freeBottom - freeTop;
  if (target.top >= freeTop && target.top + target.height <= freeBottom) return 0;
  if (!centre || target.height >= free) return Math.round(target.top - freeTop);
  return Math.round(target.top - (freeTop + (free - target.height) / 2));
}
